/**
 * TokenEconomicsPanel — "1 Ruby Token = $X" + "Default multiplier"
 * (owner 2026-10-17, mockup board 6 AdminTokens). Both live on the AI
 * registry CONFIG row; the server validates, keeps creditUnitPriceUsd at
 * $0.01 for the reports and writes the audit history shown below.
 *
 * Wallets store CREDITS (1 credit = $0.01, never changes). The token value
 * only changes how many tokens a credit is shown as, and how token-priced
 * settings (action min/max, tier allowances) convert to credits.
 *
 * A change goes through a confirm modal (custom — never native confirm)
 * showing before → after for a sample balance.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { History, Save, X } from 'lucide-react';
import { RubyGemMark } from '../../../components/RubyGemMark';
import type { AiRegistryConfigDto } from '../../../services/aiControlApi';
import type { AiTabProps } from '../AiControlCenter';

export interface TokenEconomicsPatch {
  usdPerRubyToken?: number;
  defaultMultiplier?: number;
  minPurchaseCredits?: number;
}

interface Props {
  config: AiRegistryConfigDto;
  saving: boolean;
  onSave: (patch: TokenEconomicsPatch) => Promise<void> | void;
  showToast: AiTabProps['showToast'];
}

const USD_PER_CREDIT = 0.01;
/** Sample balance for the before/after preview: 5,000 credits = $50. */
const SAMPLE_CREDITS = 5000;

/** Mirrors shared-types tokensPerCreditFor — whole tokens per credit or null. */
export function tokensPerCreditFor(x: number): number | null {
  if (!Number.isFinite(x) || x <= 0 || x > USD_PER_CREDIT) return null;
  const q = USD_PER_CREDIT / x;
  const n = Math.round(q);
  if (n < 1 || n > 1_000_000 || Math.abs(q - n) > 1e-6 * Math.max(1, n)) return null;
  return n;
}

const fmt = (n: number) => Math.round(n).toLocaleString('en-US');
/** Plain decimal, no exponent: 0.00001 → "0.00001". */
export const plainUsd = (x: number): string =>
  Number.isFinite(x) ? x.toFixed(12).replace(/0+$/, '').replace(/\.$/, '') : '';

const CARD = 'bg-white border border-stone-200 rounded-xl shadow-sm p-5 flex flex-col gap-3.5 min-w-0';
const STAT = 'p-3 rounded-lg bg-stone-50 border border-stone-200 min-w-0';

const TokenEconomicsPanel: React.FC<Props> = ({ config, saving, onSave, showToast }) => {
  const currentUsd = config.usdPerRubyToken ?? 0.00001;
  const currentMult = config.defaultMultiplier ?? 1.5;
  const [usdText, setUsdText] = useState(plainUsd(currentUsd));
  const [multText, setMultText] = useState(String(currentMult));
  const [minText, setMinText] = useState(String(config.minPurchaseCredits ?? 50));
  const [confirm, setConfirm] = useState<TokenEconomicsPatch | null>(null);

  useEffect(() => { setUsdText(plainUsd(currentUsd)); }, [currentUsd]);
  useEffect(() => { setMultText(String(currentMult)); }, [currentMult]);
  useEffect(() => { setMinText(String(config.minPurchaseCredits ?? 50)); }, [config.minPurchaseCredits]);

  const usd = Number(usdText);
  const perCredit = usdText.trim() === '' ? null : tokensPerCreditFor(usd);
  const mult = Number(multText);
  const multOk = Number.isFinite(mult) && mult > 0 && mult <= 50;
  const minPurchase = Number(minText);
  const minOk = Number.isInteger(minPurchase) && minPurchase > 0;

  const currentPer = config.tokensPerCredit ?? tokensPerCreditFor(currentUsd) ?? 1000;
  const history = useMemo(() => [...(config.history ?? [])].reverse().slice(0, 8), [config.history]);

  const requestSave = () => {
    const patch: TokenEconomicsPatch = {};
    if (perCredit === null) { showToast('$0.01 divided by the token value must be a whole number', 'error'); return; }
    if (!multOk) { showToast('Default multiplier must be above 0 and at most 50', 'error'); return; }
    if (!minOk) { showToast('Minimum purchase must be a whole number of credits', 'error'); return; }
    if (Math.abs(usd - currentUsd) > 1e-15) patch.usdPerRubyToken = usd;
    if (mult !== currentMult) patch.defaultMultiplier = mult;
    if (minPurchase !== config.minPurchaseCredits) patch.minPurchaseCredits = minPurchase;
    if (Object.keys(patch).length === 0) { showToast('Nothing changed', 'info'); return; }
    setConfirm(patch);
  };

  return (
    <>
      <div className="flex flex-wrap gap-4 items-stretch">
        <section className={`${CARD} flex-[1_1_380px]`} aria-labelledby="ruby-token-value-h">
          <h2 id="ruby-token-value-h" className="text-[15px] font-semibold text-stone-900 flex items-center gap-2">
            <RubyGemMark size={16} /> Ruby Token value
          </h2>
          <p className="text-[13px] text-stone-600 leading-snug">
            The rate every charge uses: AI actions, storage, identity checks, social posts. Wallets
            keep their dollar value when it changes — only the number of tokens shown moves.
          </p>
          <div className="flex gap-2.5 items-center flex-wrap">
            <span className="text-[15px] font-semibold text-stone-900">1 Ruby Token =</span>
            <label className={`h-12 flex items-center gap-1 px-3.5 rounded-lg border-2 ${perCredit === null ? 'border-stone-400 bg-white' : 'border-[#DC143C] bg-rose-50'}`}>
              <span className="font-mono text-[15px] text-stone-600">$</span>
              <input
                aria-label="USD per Ruby Token" inputMode="decimal" value={usdText}
                onChange={(e) => setUsdText(e.target.value)}
                className="w-32 border-0 bg-transparent font-mono text-[17px] font-semibold text-stone-900 outline-none"
              />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            <div className={STAT}>
              <div className="font-mono text-[15px] font-semibold text-stone-900 truncate">{perCredit ? fmt(perCredit * 100) : '—'}</div>
              <div className="text-[12px] text-stone-600">tokens per $1</div>
            </div>
            <div className={STAT}>
              <div className="font-mono text-[15px] font-semibold text-stone-900 truncate">{perCredit ? fmt(perCredit) : '—'}</div>
              <div className="text-[12px] text-stone-600">tokens per credit</div>
            </div>
            <div className={STAT}>
              <div className="font-mono text-[15px] font-semibold text-stone-900">$0.01</div>
              <div className="text-[12px] text-stone-600">per credit</div>
            </div>
          </div>
          <p className={`text-[12px] leading-snug ${perCredit === null ? 'text-[#9F1239] font-medium' : 'text-stone-500'}`}>
            $0.01 ÷ this value must be a whole number, so credits and tokens convert exactly —
            $0.00001 = 1,000 per credit, $0.000001 = 10,000, $0.0001 = 100.
          </p>
        </section>

        <section className={`${CARD} flex-[1_1_380px]`} aria-labelledby="default-multiplier-h">
          <h2 id="default-multiplier-h" className="text-[15px] font-semibold text-stone-900">Default multiplier</h2>
          <p className="text-[13px] text-stone-600 leading-snug">
            Used when an AI action or a storage item has no multiplier of its own. Each one can still
            be changed on its own row.
          </p>
          <label className="h-12 w-40 flex items-center gap-1.5 px-3.5 rounded-lg border border-stone-300 bg-white">
            <input
              aria-label="Default multiplier" inputMode="decimal" value={multText}
              onChange={(e) => setMultText(e.target.value)}
              className="w-20 border-0 bg-transparent font-mono text-[17px] font-semibold outline-none"
            />
            <span className="font-mono text-[15px] text-stone-600">×</span>
          </label>
          <div className="px-3.5 py-3 rounded-lg bg-stone-50 border border-stone-200 text-[13px] text-stone-700">
            {multOk ? (
              <>When a vendor charges <strong>$1.00</strong>, the tenant pays <strong>${mult.toFixed(2)}</strong> in
                tokens and RubyTill keeps <strong>${(mult - 1).toFixed(2)}</strong>.
                {mult < 1 && <span className="text-[#9F1239]"> Below 1× sells under cost.</span>}</>
            ) : 'Enter a multiplier above 0 and at most 50.'}
          </div>
          <label className="block mt-auto">
            <span className="text-[11px] uppercase tracking-wide text-stone-500 font-semibold block mb-1">
              Minimum purchase (credits)
            </span>
            <input
              aria-label="Minimum purchase (credits)" inputMode="numeric" value={minText}
              onChange={(e) => setMinText(e.target.value)}
              className="w-40 px-3 py-2 min-h-[44px] text-sm border border-stone-200 rounded-lg bg-white text-stone-800 focus:ring-2 focus:ring-rose-500"
            />
          </label>
        </section>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
            <History size={14} className="text-stone-500" /> Change history
          </h2>
          <button
            type="button" onClick={requestSave} disabled={saving}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] text-xs font-semibold bg-[#DC143C] text-white rounded-lg hover:bg-[#9F1239] disabled:opacity-40"
          >
            {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Save size={14} />}
            Save token settings
          </button>
        </div>
        {history.length === 0 ? (
          <p className="px-4 py-3 text-[12px] text-stone-500">No changes recorded yet — running on the defaults.</p>
        ) : (
          <ul className="divide-y divide-stone-100">
            {history.map((h, i) => (
              <li key={`${h.at}-${i}`} className="px-4 py-2.5 text-[12px] text-stone-700 flex flex-wrap gap-x-3 gap-y-1">
                <span className="text-stone-500 font-mono">{new Date(h.at).toLocaleString()}</span>
                <span className="font-medium">{h.byEmail || h.by}</span>
                <span className="min-w-0 break-words">
                  {h.changes.map((c) => `${LABELS[c.field] ?? c.field}: ${show(c.field, c.from)} → ${show(c.field, c.to)}`).join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirm && (
        <ConfirmEconomicsModal
          patch={confirm}
          before={{ usd: currentUsd, per: currentPer, mult: currentMult, min: config.minPurchaseCredits }}
          saving={saving}
          onCancel={() => setConfirm(null)}
          onConfirm={async () => { await onSave(confirm); setConfirm(null); }}
        />
      )}
    </>
  );
};

const LABELS: Record<string, string> = {
  usdPerRubyToken: '1 Ruby Token', defaultMultiplier: 'Default multiplier',
  creditUnitPriceUsd: 'Credit price', minPurchaseCredits: 'Minimum purchase',
};
function show(field: string, v: unknown): string {
  if (v === null || v === undefined) return 'default';
  if (typeof v !== 'number') return String(v);
  if (field === 'usdPerRubyToken' || field === 'creditUnitPriceUsd') return `$${plainUsd(v)}`;
  if (field === 'defaultMultiplier') return `${v}×`;
  return fmt(v);
}

const ConfirmEconomicsModal: React.FC<{
  patch: TokenEconomicsPatch;
  before: { usd: number; per: number; mult: number; min: number };
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ patch, before, saving, onCancel, onConfirm }) => {
  const afterUsd = patch.usdPerRubyToken ?? before.usd;
  const afterPer = tokensPerCreditFor(afterUsd) ?? before.per;
  const afterMult = patch.defaultMultiplier ?? before.mult;
  const rows: Array<[string, string, string]> = [];
  if (patch.usdPerRubyToken !== undefined) {
    rows.push(['1 Ruby Token', `$${plainUsd(before.usd)}`, `$${plainUsd(afterUsd)}`]);
    rows.push(['Tokens per credit', fmt(before.per), fmt(afterPer)]);
    rows.push([`A $${(SAMPLE_CREDITS * USD_PER_CREDIT).toFixed(2)} balance shows as`,
      `${fmt(SAMPLE_CREDITS * before.per)} tokens`, `${fmt(SAMPLE_CREDITS * afterPer)} tokens`]);
    rows.push(['An action with a 6,000-token minimum charges at least',
      `${Math.ceil(6000 / before.per)} credits`, `${Math.ceil(6000 / afterPer)} credits`]);
  }
  if (patch.defaultMultiplier !== undefined) {
    rows.push(['Default multiplier', `${before.mult}×`, `${afterMult}×`]);
    rows.push(['$1.00 vendor cost charges', `$${before.mult.toFixed(2)}`, `$${afterMult.toFixed(2)}`]);
  }
  if (patch.minPurchaseCredits !== undefined) {
    rows.push(['Minimum purchase', `${fmt(before.min)} credits`, `${fmt(patch.minPurchaseCredits)} credits`]);
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" role="dialog" aria-modal="true" aria-labelledby="econ-confirm-h">
      <div className="w-full max-w-lg bg-white rounded-xl overflow-hidden shadow-xl">
        <div className="bg-black px-5 py-4 flex items-center gap-3 border-b-[3px] border-[#DC143C]">
          <span className="w-9 h-9 rounded-lg bg-[#DC143C] flex items-center justify-center flex-shrink-0">
            <RubyGemMark size={18} color="#ffffff" />
          </span>
          <h3 id="econ-confirm-h" className="text-white font-semibold text-[15px] flex-1">Change Ruby Token settings?</h3>
          <button type="button" onClick={onCancel} aria-label="Close" className="w-11 h-11 flex items-center justify-center text-white/70 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[13px] text-stone-600">
            Applies to every tenant within a minute. Balances keep their dollar value; stored credits do not change.
          </p>
          <div className="border border-stone-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-2 px-3 py-2 bg-stone-50 text-[11px] uppercase tracking-wide text-stone-500 font-semibold">
              <span /> <span>Now</span> <span>After</span>
            </div>
            {rows.map(([label, a, b]) => (
              <div key={label} className="grid grid-cols-[1.4fr_1fr_1fr] gap-2 px-3 py-2 text-[13px] border-t border-stone-100">
                <span className="text-stone-700">{label}</span>
                <span className="font-mono text-stone-500">{a}</span>
                <span className="font-mono font-semibold text-stone-900">{b}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onCancel} className="px-4 min-h-[44px] text-sm font-medium text-stone-700 border border-stone-300 rounded-lg hover:bg-stone-50">
              Cancel
            </button>
            <button type="button" onClick={onConfirm} disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 min-h-[44px] text-sm font-semibold bg-black text-white rounded-lg hover:bg-stone-800 disabled:opacity-40">
              {saving && <RubyGemMark size={14} drawing color="currentColor" />}
              Confirm change
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenEconomicsPanel;
